import { message } from 'antd';
import { buildCanonicalPrefill } from '../../util/ehr';
import { makeKeyedSingleFlight } from '../../utils/singleFlight';

const pick = (source, keys = []) => {
  if (!source) {
    return undefined;
  }

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const value = source[key];
      if (value !== undefined) {
        return value;
      }
    }
  }

  return undefined;
};

const sanitizeForFileName = (value) => {
  if (!value || typeof value !== 'string') {
    return undefined;
  }

  return value.replace(/\s+/g, '_');
};

const sanitizeDateForFileName = (value) => {
  const sanitized = sanitizeForFileName(value);
  return sanitized ? sanitized.replace(/\//g, '-') : undefined;
};

const ensureNormalized = (record, extras = {}) => {
  if (record && record.__isNormalized) {
    return record;
  }

  return normalizeRecord(record, extras);
};

export const normalizeRecord = (record = {}, extras = {}) => {
  const normalized = {
    sendToken:
      pick(extras, ['sendToken'])
      || pick(record, ['sendToken', 'send_token', 'record'])
      || undefined,

    receiverType:
      pick(extras, ['receiverType'])
      || pick(record, ['receiverType', 'receiver_type'])
      || undefined,

    receiver:
      pick(extras, ['receiver'])
      || pick(record, ['receiver', 'receiver_phone', 'fax_number'])
      || undefined,

    patientName:
      pick(extras, ['patientName'])
      || pick(record, ['patientName', 'patient_name'])
      || undefined,

    patientDOB:
      pick(extras, ['patientDOB'])
      || pick(record, ['patientDOB', 'patient_dob'])
      || undefined,

    patientGender:
      pick(extras, ['patientGender'])
      || pick(record, ['patientGender', 'patient_gender'])
      || undefined,

    patientPhoneNumber:
      pick(extras, ['patientPhoneNumber'])
      || pick(record, ['patientPhoneNumber', 'patient_phone_number'])
      || undefined,

    patientAddress:
      pick(extras, ['patientAddress'])
      || pick(record, ['patientAddress', 'patient_address'])
      || undefined,

    patientCity:
      pick(extras, ['patientCity'])
      || pick(record, ['patientCity', 'patient_city'])
      || undefined,

    patientState:
      pick(extras, ['patientState'])
      || pick(record, ['patientState', 'patient_state'])
      || undefined,

    patientZipCode:
      pick(extras, ['patientZipCode'])
      || pick(record, ['patientZipCode', 'patient_zip_code'])
      || undefined,

    patientEmail:
      pick(extras, ['patientEmail'])
      || pick(record, ['patientEmail', 'patient_email'])
      || undefined,

    patientFirstName:
      pick(extras, ['patientFirstName'])
      || pick(record, ['patientFirstName', 'patient_first_name'])
      || undefined,

    patientMiddleName:
      pick(extras, ['patientMiddleName'])
      || pick(record, ['patientMiddleName', 'patient_middle_name'])
      || undefined,

    patientLastName:
      pick(extras, ['patientLastName'])
      || pick(record, ['patientLastName', 'patient_last_name'])
      || undefined,

    patientMrn:
      pick(extras, ['patientMrn'])
      || pick(record, ['patientMrn', 'patient_mrn', 'mrn'])
      || undefined,

    documentTitle:
      pick(extras, ['documentTitle'])
      ?? pick(record, ['documentTitle', 'document_title'])
      ?? undefined,

    documentCategory:
      pick(extras, ['documentCategory'])
      || pick(record, ['documentCategory', 'document_category'])
      || undefined,

    dateOfService:
      pick(extras, ['dateOfService'])
      || pick(record, ['dateOfService', 'date_of_service'])
      || undefined,

    documentType:
      pick(extras, ['documentType'])
      || pick(record, ['documentType', 'document_type'])
      || undefined,

    receivedAt:
      pick(extras, ['receivedAt'])
      || pick(record, ['receivedAt', 'received_at'])
      || undefined,

    createdAt:
      pick(extras, ['createdAt'])
      || pick(record, ['createdAt', 'created_at'])
      || undefined,

    callerName:
      Object.prototype.hasOwnProperty.call(extras, 'callerName')
        ? extras.callerName
        : pick(record, ['callerName', 'caller_name']) ?? undefined,

    senderName:
      pick(extras, ['senderName'])
      || pick(record, ['senderName', 'sender_name'])
      || undefined,

    practitionerName:
      pick(extras, ['practitionerName'])
      || pick(record, ['practitionerName', 'practitioner_name'])
      || undefined,

    referringProvider:
      pick(extras, ['referringProvider'])
      || pick(record, ['referringProvider', 'referring_provider'])
      || undefined,

    referringProviderOffice:
      pick(extras, ['referringProviderOffice'])
      || pick(record, ['referringProviderOffice', 'provider_office', 'referring_provider_office'])
      || undefined,

    locationId:
      pick(extras, ['locationId'])
      || pick(record, ['locationId', 'location_id'])
      || undefined,

    emrIntegrationCredentialId:
      pick(extras, ['emrIntegrationCredentialId'])
      || pick(record, ['emrIntegrationCredentialId', 'emr_integration_credential_id'])
      || undefined,

    parentType:
      pick(extras, ['parentType'])
      || pick(record, ['parentType', 'parent_type'])
      || undefined,

    dynamicSendToken:
      pick(extras, ['dynamicSendToken'])
      || pick(record, ['dynamicSendToken'])
      || pick(record, ['sendToken', 'send_token']),

    uploadReceiverType:
      pick(extras, ['uploadReceiverType'])
      || pick(record, ['uploadReceiverType'])
      || pick(extras, ['receiverType'])
      || pick(record, ['receiverType', 'receiver_type'])
      || undefined,

    isEmailAttachment:
      typeof pick(extras, ['isEmailAttachment']) === 'boolean'
        ? pick(extras, ['isEmailAttachment'])
        : pick(record, ['isEmailAttachment']) ?? pick(record, ['receiver_type']) === 'email_attachment',

    isEmailSubmission:
      typeof pick(extras, ['isEmailSubmission']) === 'boolean'
        ? pick(extras, ['isEmailSubmission'])
        : pick(record, ['isEmailSubmission', 'is_email_submission']) === true,

    isSentContext:
      typeof pick(extras, ['isSentContext']) === 'boolean'
        ? pick(extras, ['isSentContext'])
        : pick(record, ['isSentContext']) || false,

    fileName:
      pick(extras, ['fileName'])
      || pick(record, ['fileName'])
      || undefined,

    documentNotes:
      pick(extras, ['documentNotes'])
      || pick(record, ['documentNotes', 'document_notes'])
      || undefined,

    primaryPlan:
      pick(extras, ['primaryPlan'])
      || pick(record, ['primaryPlan', 'primary_plan'])
      || undefined,

    primaryMemberId:
      pick(extras, ['primaryMemberId'])
      || pick(record, ['primaryMemberId', 'primary_member_id'])
      || undefined,

    payerName:
      pick(extras, ['payerName'])
      || pick(record, ['payerName', 'payer_name'])
      || undefined,

    groupNumber:
      pick(extras, ['groupNumber'])
      || pick(record, ['groupNumber', 'group_number'])
      || undefined,

    currentTransmission:
      pick(extras, ['currentTransmission'])
      || pick(record, ['currentTransmission'])
      || undefined,

    apiCredentials:
      pick(extras, ['apiCredentials'])
      || pick(record, ['apiCredentials', 'api_credentials'])
      || undefined,

    chartFiled:
      pick(extras, ['chartFiled'])
      ?? pick(record, ['chartFiled', 'chart_filed'])
      ?? undefined,
  };

  const receiverType = normalized.receiverType;
  if (normalized.isEmailAttachment === undefined && receiverType) {
    normalized.isEmailAttachment = receiverType === 'email_attachment';
  }

  if (!normalized.dynamicSendToken) {
    normalized.dynamicSendToken = normalized.sendToken;
  }

  if (!normalized.uploadReceiverType && receiverType) {
    normalized.uploadReceiverType = receiverType;
  }

  normalized.__isNormalized = true;

  return normalized;
};

export const computeRecordFileName = (inputRecord = {}) => {
  const record = ensureNormalized(inputRecord);

  if (record.fileName) {
    return record.fileName;
  }

  if (record.documentTitle) {
    return record.documentTitle;
  }

  const receivedAt = sanitizeForFileName(record.receivedAt);
  const callerName = sanitizeForFileName(record.callerName);
  const senderName = sanitizeForFileName(record.senderName);
  const createdAt = sanitizeForFileName(record.createdAt);

  if (record.receiverType === 'uploaded_record') {
    return `${sanitizeDateForFileName(record.createdAt || record.receivedAt) || 'unknown-date'}_uploaded_record`;
  }

  if (!record.isEmailAttachment && !record.isSentContext && receivedAt && callerName) {
    return `${receivedAt}_${callerName}`;
  }

  if (createdAt && (senderName || callerName)) {
    return `${createdAt}_${senderName || callerName}`;
  }

  if (record.patientName) {
    return sanitizeForFileName(record.patientName) || 'record';
  }

  return 'record';
};

export const computeEmaStandaloneTaskFileName = ({
  emr,
  associateDoc,
  patientFirstName = '',
  patientMrn = '',
  fileToChart = false,
  record = {},
}) => {
  const hasPatientLookup = patientFirstName.trim() !== '' || patientMrn.trim() !== '';
  if (emr !== 'EMA' || !associateDoc || !hasPatientLookup || fileToChart) {
    return undefined;
  }

  return computeRecordFileName(record);
};

export const buildCreateTaskModalData = (inputRecord = {}, options = {}) => {
  const { receiver, ...extras } = options;
  const record = ensureNormalized(inputRecord, extras);
  const documentTitle = pick(extras, ['documentTitle'])
    ?? pick(inputRecord, ['documentTitle', 'document_title'])
    ?? record.documentTitle;
  const chartFiled = pick(extras, ['chartFiled'])
    ?? pick(receiver, ['chartFiled', 'chart_filed'])
    ?? record.chartFiled;

  return {
    name: record.patientName,
    patient_first_name: record.patientFirstName,
    patient_middle_name: record.patientMiddleName,
    patient_last_name: record.patientLastName,
    dob: record.patientDOB,
    record: record.sendToken,
    practitionerName: record.practitionerName,
    receiverType: record.receiverType,
    receivedAt: record.receivedAt,
    callerName: record.callerName,
    apiCredentials: record.apiCredentials,
    fileName: computeRecordFileName({
      ...record,
      documentTitle,
    }),
    chartFiled: chartFiled === true,
  };
};

const buildDynamicPrefill = (record, overrides = {}) => {
  const source = {
    patientFirstName: record.patientFirstName,
    patientMiddleName: record.patientMiddleName,
    patientLastName: record.patientLastName,
    patientDOB: record.patientDOB,
    patientGender: record.patientGender,
    patientPhoneNumber: record.patientPhoneNumber,
    patientAddress: record.patientAddress,
    patientCity: record.patientCity,
    patientState: record.patientState,
    patientZipCode: record.patientZipCode,
    patientEmail: record.patientEmail,
    patientMrn: record.patientMrn,
    documentCategory: record.documentCategory,
    documentTitle: record.documentTitle,
    dateOfService: record.dateOfService,
    fileName: computeRecordFileName(record),
    primary_plan: record.primaryPlan,
    primary_member_id: record.primaryMemberId,
    payer_name: record.payerName,
    group_number: record.groupNumber,
    secondary_payer_name: record.secondaryPayerName,
    secondary_member_id: record.secondaryMemberId,
    secondary_group_number: record.secondaryGroupNumber,
    icd_codes: record.icdCodes,
    marketingRep: record.marketingRep,
    referring_provider: record.referringProvider,
    provider_office: record.referringProviderOffice,
    location_id: record.locationId,
    notes: record.documentNotes,
  };

  return buildCanonicalPrefill({
    ...source,
    ...overrides,
  });
};

const showDirectAutoUploadResult = (result) => {
  if (result?.error) {
    message.error({
      content: 'Unable to autoupload document.',
    });
    return;
  }

  message.success({
    content: 'Record will be uploaded shortly. We will notify you of any issues via email.',
  });
};

const showSftpUploadResult = (result) => {
  if (result?.canceled) {
    return;
  }

  if (!result?.error) {
    message.success({
      content: 'Successfully uploaded to SFTP Server.',
    });
    return;
  }

  const errorCode = result?.response?.status;
  const errorMessage = result?.response?.data?.error;
  if (errorCode === 400) {
    message.error({
      content: 'An SFTP Folder does not exist for the selected Fax Number. Please contact a Medsender admin to create a Fax Folder.',
    });
    return;
  }

  if (errorCode === 403) {
    message.error({
      content: errorMessage === 'No SFTP Credential found on your account.'
        ? 'Your SFTP service is missing credentials. Please contact Medsender support to add credentials.'
        : 'You do not have access to this feature.',
    });
    return;
  }

  message.error({
    content: 'We were unable to upload to the SFTP Server. Please try again.',
  });
};

export const handleDirectAutoUpload = makeKeyedSingleFlight(
  async ({ triggerAutoUpload, sendToken, receiverType }) => {
    if (typeof triggerAutoUpload !== 'function') {
      return undefined;
    }

    const result = await triggerAutoUpload({ sendToken, receiverType });
    showDirectAutoUploadResult(result);
    return result;
  },
  ({ sendToken }) => sendToken,
);

export const handleSftpUpload = makeKeyedSingleFlight(
  async ({ triggerSFTPUpload, sendToken }) => {
    if (typeof triggerSFTPUpload !== 'function') {
      return undefined;
    }

    const result = await triggerSFTPUpload(sendToken);
    showSftpUploadResult(result);
    return result;
  },
  ({ sendToken }) => sendToken,
);

const openDynamicDrawer = ({
  record,
  dependencies,
  title,
  workflow,
  prefillOverrides,
}) => {
  const prefill = buildDynamicPrefill(record, prefillOverrides);
  const drawerData = {
    title,
    workflow,
    prefill,
    parentType: record.parentType,
    sendToken: record.dynamicSendToken || record.sendToken,
    emrIntegrationCredentialId: record.emrIntegrationCredentialId,
    providerHints: {
      orderingProviderName: record.practitionerName || undefined,
      referringProviderName: record.referringProvider || undefined,
    },
  };

  if (dependencies.onOpenEhrDrawer) {
    dependencies.onOpenEhrDrawer(drawerData);
  }
};

export const handleSendToChart = ({
  record: inputRecord,
  dependencies,
  dynamicTitle = 'Send to Chart',
  prefillOverrides,
}) => {
  if (!inputRecord) {
    return;
  }

  const record = ensureNormalized(inputRecord);

  openDynamicDrawer({
    record,
    dependencies,
    title: dynamicTitle,
    workflow: 'send_to_chart',
    prefillOverrides,
  });
};

export const handleCreateChart = ({
  record: inputRecord,
  dependencies,
  dynamicTitle = 'Create Patient Chart',
  prefillOverrides,
}) => {
  if (!inputRecord) {
    return;
  }

  const record = ensureNormalized(inputRecord);

  openDynamicDrawer({
    record,
    dependencies,
    title: dynamicTitle,
    workflow: 'create_chart',
    prefillOverrides,
  });
};

export const handleCreateTask = ({
  record: inputRecord,
  dependencies,
  dynamicTitle = 'Create Task',
  prefillOverrides,
}) => {
  if (!inputRecord) {
    return;
  }

  const record = ensureNormalized(inputRecord);

  openDynamicDrawer({
    record,
    dependencies,
    title: dynamicTitle,
    workflow: 'task_creation',
    prefillOverrides,
  });
};
